import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';

function inferType(message, explicitType) {
  if (explicitType === 'error' || explicitType === 'warning' || explicitType === 'success' || explicitType === 'info') {
    return explicitType;
  }
  const value = String(message || '').trim();
  if (value.startsWith('❌')) return 'error';
  if (value.startsWith('⚠️')) return 'warning';
  if (value.startsWith('✅')) return 'success';
  return 'info';
}

function cleanMessage(message) {
  return String(message || '')
    .replace(/^(✅|❌|⚠️)\s*/, '')
    .trim();
}

const TYPE_STYLES = {
  success: {
    panel: 'border-emerald-200 bg-white text-emerald-900',
    halo: 'from-emerald-400/30 via-emerald-300/45 to-emerald-400/30',
    chip: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    icon: 'text-emerald-600 border-emerald-200',
    Icon: CheckCircle2,
    label: 'Success',
  },
  error: {
    panel: 'border-rose-200 bg-white text-rose-900',
    halo: 'from-rose-400/30 via-rose-300/45 to-rose-400/30',
    chip: 'bg-rose-100 text-rose-700 border-rose-200',
    icon: 'text-rose-600 border-rose-200',
    Icon: AlertTriangle,
    label: 'Error',
  },
  warning: {
    panel: 'border-amber-200 bg-white text-amber-900',
    halo: 'from-amber-400/30 via-amber-300/45 to-amber-400/30',
    chip: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: 'text-amber-600 border-amber-200',
    Icon: AlertTriangle,
    label: 'Warning',
  },
  info: {
    panel: 'border-brand/25 bg-white text-[#0f172a]',
    halo: 'from-brand/30 via-brand/45 to-brand/30',
    chip: 'bg-brand-soft text-brand border-brand/25',
    icon: 'text-brand border-brand/25',
    Icon: Info,
    label: 'Info',
  },
};

export default function CenteredStatusPopup({ message = '', type }) {
  const rawMessage = String(message || '').trim();
  const [dismissedMessage, setDismissedMessage] = useState('');
  const messageKey = useMemo(() => rawMessage, [rawMessage]);

  useEffect(() => {
    if (!messageKey) {
      setDismissedMessage('');
    }
  }, [messageKey]);

  const open = Boolean(messageKey) && dismissedMessage !== messageKey;
  const resolvedType = inferType(message, type);
  const style = TYPE_STYLES[resolvedType] || TYPE_STYLES.info;
  const Icon = style.Icon;
  const text = cleanMessage(message);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="merchant-centered-status-popup"
          className="fixed inset-0 z-[140] pointer-events-none flex items-center justify-center px-4 bg-black/18 backdrop-blur-[1.5px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            initial={{ y: 24, scale: 0.95, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 10, scale: 0.98, opacity: 0 }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
            className="pointer-events-none relative max-w-2xl w-full"
            role="status"
            aria-live="polite"
          >
            <div className={`absolute -inset-0.5 rounded-[1.7rem] bg-gradient-to-r ${style.halo} blur-md`} />
            <div className={`relative rounded-3xl border shadow-2xl px-7 py-6 pointer-events-auto ${style.panel}`}>
              <button
                type="button"
                onClick={() => setDismissedMessage(messageKey)}
                className="absolute top-3 right-3 h-8 w-8 rounded-full border border-stone-200 bg-white/80 text-stone-500 hover:text-stone-700 hover:bg-white inline-flex items-center justify-center"
                aria-label="Close notification"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center justify-between gap-3">
                <div className={`inline-flex h-7 px-2.5 rounded-full border text-[11px] font-bold uppercase tracking-wider ${style.chip}`}>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
                    {style.label}
                  </span>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <span className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white border ${style.icon}`}>
                  <Icon className="w-6 h-6" />
                </span>
                <p className="text-xl font-bold leading-snug">{text}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
